import { useEffect, useState } from "react"
import { Box, Button, Fade, Paper, Stack, TextField, Typography, Zoom } from "@mui/material"
import CheckIcon from '@mui/icons-material/Check'
import type { EtapaPasso, SelecoesPassos } from "../passos"
import { itensSelecionadosTexto, numerosDasEtapas, tituloDaEtapa } from "./passo-utils"
import { useMostrarAoAbrir } from "./use-acompanhar-passo"

interface StepSummaryProps {
    etapas: EtapaPasso[]
    selecoes: SelecoesPassos
    onEditar: (indice: number) => void
    /** Observação do item — só aparece quando quem chama passa o handler. */
    observacao?: string
    onObservacaoChange?: (valor: string) => void
}

function CheckBadge({ size }: { size: number }): JSX.Element {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
            }}
        >
            <CheckIcon sx={{ fontSize: size * 0.55 }} />
        </Box>
    )
}

function StepSummary({ etapas, selecoes, onEditar, observacao, onObservacaoChange }: StepSummaryProps): JSX.Element {
    const [animar, setAnimar] = useState(false)
    const numeros = numerosDasEtapas(etapas)
    const { lista, alvo: confirmacao } = useMostrarAoAbrir()

    useEffect(() => {
        const id = requestAnimationFrame(() => setAnimar(true))
        return () => cancelAnimationFrame(id)
    }, [])

    return (
        <Stack
            ref={lista}
            spacing={1.5}
            sx={{
                flexGrow: 1,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                p: 2,
                backgroundColor: 'background.default',
                '& > *': { flexShrink: 0 },
            }}
        >
            {etapas.map((etapa, indice) => {
                const texto = itensSelecionadosTexto(etapa.passo, selecoes[etapa.chave] ?? {})
                if (!texto) return null

                const adicional = etapa.pai !== null

                return (
                    <Box key={etapa.chave} sx={{ pl: adicional ? 5 : 0 }}>
                        <Paper
                            elevation={0}
                            sx={{ borderRadius: 3, border: '1.5px solid', borderColor: 'divider' }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: adicional ? 1.5 : 2 }}>
                                <CheckBadge size={adicional ? 24 : 32} />

                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography variant={adicional ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                        {tituloDaEtapa(etapa, numeros[indice] ?? indice + 1)}
                                    </Typography>

                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        {texto}
                                    </Typography>
                                </Box>

                                <Button
                                    onClick={() => onEditar(indice)}
                                    sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                                >
                                    Editar
                                </Button>
                            </Stack>
                        </Paper>
                    </Box>
                )
            })}

            {onObservacaoChange && (
                <TextField
                    label="Observação (opcional)"
                    value={observacao ?? ''}
                    onChange={(e) => onObservacaoChange(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                />
            )}

            <Stack ref={confirmacao} spacing={1.5} alignItems="center" sx={{ textAlign: 'center', py: 3 }}>
                <Zoom in={animar} timeout={400} style={{ transitionDelay: animar ? '120ms' : '0ms' }}>
                    <Box>
                        <CheckBadge size={72} />
                    </Box>
                </Zoom>

                <Fade in={animar} timeout={400} style={{ transitionDelay: animar ? '320ms' : '0ms' }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            Item personalizado!
                        </Typography>

                        <Typography variant="body1" color="text.secondary">
                            Confira o total e adicione ao pedido.
                        </Typography>
                    </Box>
                </Fade>
            </Stack>
        </Stack>
    )
}

export default StepSummary